/* ChatTabs.jsx ----------------------------------------------------------- */
import {
  Box,
  HStack,
  Text,
  useColorModeValue,
  Icon as ChakraIcon,
  Flex,
  Circle,
  VStack,
  Tooltip,
} from "@chakra-ui/react";
import { FaHotel, FaPlane, FaLock, FaCheck } from "react-icons/fa";
import { FiMessageSquare, FiMapPin } from "react-icons/fi";
import { ReactComponent as FlagIcon } from "../../assets/icons/flag.svg";
import { ReactComponent as CabinIcon } from "../../assets/icons/home_icon.svg";
import { ReactComponent as TransportationIcon } from "../../assets/icons/transportation.svg";
import Translate from "../../components/LanguageTranslation/Translate";

const STEP_META = [
  {
    key: "general_conversation",
    label: "General",
    icon: FiMessageSquare,
    description: "Start your conversation",
  },
  {
    key: "transportation_conversation",
    label: "Transportation",
    icon: TransportationIcon,
    description: "Plan your travel",
  },
  {
    key: "accommodation_conversation",
    label: "Accommodation",
    icon: CabinIcon,
    description: "Find places to stay",
  },
  {
    key: "explore_conversation",
    label: "Activities",
    icon: FlagIcon,
    description: "Discover things to do",
  },
];

export default function ChatTabs({ active, onChange, generalInfoAdded, generalInfo, hasHomeToGoData }) {
  // console.log("🔍 ChatTabs.js - props - hasHomeToGoData:", hasHomeToGoData);
  
  /* palette */
  const activeClr = useColorModeValue("#286328", "#286328");
  const idleClr = useColorModeValue("gray.600", "gray.300");
  const stepBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const dividerClr = useColorModeValue("blackAlpha.200", "whiteAlpha.300");
  const completedClr = useColorModeValue("#286328", "#286328");
  const completedBgClr = useColorModeValue("#e8f5e8", "#1a3d1a");
  const activeBgClr = useColorModeValue("#e8f5e8", "#1a3d1a");

  const getCurrentStepIndex = () => {
    return STEP_META.findIndex((step) => step.key === active);
  };

  // Get the context of what needs to be completed to unlock the current tab
  const getPreviousTabContext = (tabKey) => {
    // For transportation tab, need to complete general tab
    if (tabKey === "transportation_conversation") {
      return "Complete the 'General' step to add your trip details first";
    }
    
    // For accommodation and activities tabs, need to complete general tab
    if (tabKey === "accommodation_conversation" || tabKey === "explore_conversation") {
      return "Complete the 'General' step to unlock all planning features";
    }
    
    return "Complete the 'General' step to begin planning"; // fallback
  };

  // Check if a tab is completed (has actual data present)
  const isTabCompleted = (tabKey) => {
    // General tab: completed when there's meaningful generalInfo data
    if (tabKey === "general_conversation") {
      return generalInfo && Object.keys(generalInfo).length > 0;
    }
    
    // Transportation tab: completed when there's transportation data
    if (tabKey === "transportation_conversation") {
      return generalInfo && 
             generalInfo.flight_info && 
             generalInfo.flight_info.length > 0;
    }
    
    // Accommodation tab: completed when there's accommodation data
    if (tabKey === "accommodation_conversation") {
      return hasHomeToGoData;
    }
    
    // Activities tab: completed when there are activities planned
    if (tabKey === "explore_conversation") {
      // You might want to add a prop to check for activities data
      // For now, we'll consider it completed if accommodation is completed
      return hasHomeToGoData;
    }
    
    return false;
  };

  // Progressive unlocking logic - each tab requires ALL previous tabs to be completed
  const isTabUnlocked = (tabKey) => {
    // General tab is always unlocked
    if (tabKey === "general_conversation") {
      return true;
    }

    // If no generalInfo, only general tab is unlocked
    if (!generalInfo || Object.keys(generalInfo).length === 0) {
      return false;
    }

    // Once transportation tab is unlocked (generalInfo has data), 
    // all subsequent tabs are also unlocked
    if (tabKey === "transportation_conversation" || 
        tabKey === "accommodation_conversation" || 
        tabKey === "explore_conversation") {
      return generalInfo && Object.keys(generalInfo).length > 0;
    }

    return false;
  };

  const handleStepClick = (stepKey) => {
    if (onChange && isTabUnlocked(stepKey)) {
      onChange(stepKey);
    }
  };

  return (
    <Box
      m={1}
      mt={0}
      h="66px"
      w={{ base: "97vw", md: "auto" }}
      overflowX="auto"
    >
      <Box
        w="full"
        border="1px solid"
        borderColor={dividerClr}
        borderRadius="lg"
        p={1}
        overflow="hidden"
        overflowX="auto"
        bg="white"
        _dark={{ bg: "gray.800" }}
        position="relative"
      >
        <Flex
          gap={2}
          align="center"
          justify="space-between"
          position="relative"
        >
          {STEP_META.map(({ key, label, icon: Icon, description }, index) => {
            const locked = !isTabUnlocked(key);
            const isActive = key === active;
            const isCompleted = isTabCompleted(key);
            
            if (key === "explore_conversation") {
              {/* console.log("🔍 ChatTabs.js - explore_conversation - locked:", locked, "isActive:", isActive, "isCompleted:", isCompleted); */}
            }

            return (
              <Tooltip
                label={
                  locked
                    ? getPreviousTabContext(key)
                    : ""
                }
                placement="top"
                hasArrow
                bg="yellow.100"
                color="black"
                border="1px solid"
                borderColor="yellow.300"
                borderRadius="md"
                fontSize="sm"
                fontStyle="italic"
                isDisabled={!locked}
              >
                <Flex
                  key={key}
                  direction="column"
                  align="center"
                  flex="1"
                  minW="120px"
                  cursor={locked ? "not-allowed" : "pointer"}
                  onClick={() => !locked && handleStepClick(key)}
                  opacity={locked ? 0.6 : 1}
                  transition="all 0.2s"
                  _hover={!locked ? { transform: "translateY(-2px)" } : {}}
                  position="relative"
                  zIndex={1}
                >
                  <Circle
                    size="30px"
                    bg={
                      locked
                        ? stepBg
                        : isActive
                        ? activeBgClr
                        : isCompleted
                        ? completedBgClr
                        : stepBg
                    }
                    color={
                      locked
                        ? idleClr
                        : isActive
                        ? activeClr
                        : isCompleted
                        ? completedClr
                        : idleClr
                    }
                    border="2px solid"
                    borderColor={
                      locked
                        ? dividerClr
                        : isActive
                        ? activeClr
                        : isCompleted
                        ? completedClr
                        : dividerClr
                    }
                    borderStyle={isActive ? "dashed" : "solid"}
                    mb={2}
                    transition="all 0.2s"
                  >
                    <ChakraIcon
                      as={locked ? FaLock : isCompleted ? FaCheck : Icon}
                      boxSize={4}
                      fill={
                        locked
                          ? "currentColor"
                          : isActive || isCompleted
                          ? "currentColor"
                          : "currentColor"
                      }
                    />
                  </Circle>

                  <VStack spacing={1} align="center">
                    <Text
                      fontSize={{ base: "xs", md: "xs" }}
                      fontWeight="semibold"
                      color={
                        locked
                          ? idleClr
                          : isActive
                          ? activeClr
                          : isCompleted
                          ? completedClr
                          : idleClr
                      }
                      textAlign="center"
                      lineHeight="1.2"
                      mb={0}
                    >
                      <Translate text={label} />
                    </Text>
                  </VStack>
                </Flex>
              </Tooltip>
            );
          })}
        </Flex>

        {/* Progress lines connecting the steps */}
        {STEP_META.map((step, index) => {
          if (index === STEP_META.length - 1) return null;

          const isCompleted = index < getCurrentStepIndex();
          const nextStepLocked = !isTabUnlocked(STEP_META[index + 1].key);

          return (
            <Box
              key={`progress-${index}`}
              position="absolute"
              left={`calc(${100 / STEP_META.length}% * ${index + 1})`}
              top="20px"
              w={`calc(${100 / STEP_META.length}% - 40px)`}
              h="2px"
              bg={
                nextStepLocked
                  ? dividerClr
                  : isCompleted
                  ? completedClr
                  : dividerClr
              }
              transform="translateX(-50%)"
              zIndex={0}
            />
          );
        })}
      </Box>
    </Box>
  );
}
